import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ExternalDataService, Customer, location } from '../../../services/externalDataService.service';

export interface CreateCustomerAddress {
  customerId: string;
  address: string;
  pincode: string;
  location: location;
  locationName: string;
  isPrimary: boolean;
  addressType?: string | null;
}

@Component({
  selector: 'app-add-customer-address',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './add-customer-address.component.html',
  styleUrl: './add-customer-address.component.css'
})
export class AddCustomerAddressComponent {
  @Input() customer: Customer | null = null;
  @Input() customerId: string = '';
  @Input() showModal = false;
  @Output() addressCreated = new EventEmitter<void>();
  @Output() closeModal = new EventEmitter<void>();

  addressForm: FormGroup;
  isLoading = false;
  errorMessage = '';

  constructor(
    private fb: FormBuilder,
    private externalDataService: ExternalDataService
  ) {
    this.addressForm = this.fb.group({
      address: ['', [Validators.required, Validators.minLength(10)]],
      pincode: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
      locationName: ['', [Validators.required]],
      latitude: [0, [Validators.required]],
      longitude: [0, [Validators.required]],
      isPrimary: [false],
      addressType: ['']
    });
  }

  onSubmit(): void {
    if (this.addressForm.valid && (this.customer || this.customerId)) {
      this.isLoading = true;
      this.errorMessage = '';

      const formValue = this.addressForm.value;
      const targetCustomerId = this.customerId || (this.customer?.customerId || '');
      const addressData: CreateCustomerAddress = {
        customerId: targetCustomerId,
        address: formValue.address,
        pincode: formValue.pincode,
        location: {
          latitude: formValue.latitude,
          longitude: formValue.longitude
        },
        locationName: formValue.locationName,
        isPrimary: formValue.isPrimary,
        addressType: formValue.addressType || null
      };

      this.externalDataService.createCustomerAddress(addressData)
        .subscribe({
          next: () => {
            this.isLoading = false;
            this.addressCreated.emit();
            this.resetForm();
          },
          error: (error) => {
            this.isLoading = false;
            this.errorMessage = error.error?.message || 'Failed to create address. Please try again.';
          }
        });
    }
  }

  onCancel(): void {
    this.closeModal.emit();
    this.resetForm();
  }

  private resetForm(): void {
    this.addressForm.reset({
      address: '',
      pincode: '',
      locationName: '',
      latitude: 0,
      longitude: 0,
      isPrimary: false,
      addressType: ''
    });
    this.errorMessage = '';
  }

  // Method to set location from map selector
  setLocation(lat: number, lng: number): void {
    this.addressForm.patchValue({
      latitude: lat,
      longitude: lng
    });
  }
}
